import { useCallback, useMemo, useState } from 'react';
import { LatLngBounds, LatLngExpression } from 'leaflet';
import { Gate } from '../types/Gate';
import { GateSummary, GateSummaryWithCluster } from '../types/GateSummary';
import {
  transformGateToSummary,
  transformGateToSummaryWithCluster,
  calculateAllBounds,
  calculateSelectedBounds,
  FALLBACK_CENTER,
} from '../utils/gateMapUtils';

export function useGateSummaries(gates: Gate[] | undefined): GateSummary[] {
  return useMemo<GateSummary[]>(() => {
    if (!gates) return [];
    return gates.map((gate, index) => transformGateToSummary(gate, index)).filter(Boolean) as GateSummary[];
  }, [gates]);
}

export function useGateSummariesWithCluster(gates: Gate[] | undefined): GateSummaryWithCluster[] {
  return useMemo<GateSummaryWithCluster[]>(() => {
    if (!gates) return [];
    return gates
      .map((gate, index) => transformGateToSummaryWithCluster(gate, index))
      .filter(Boolean) as GateSummaryWithCluster[];
  }, [gates]);
}

export type FocusMode = 'all' | 'selected';

export interface UseGateFocusOptions {
  initialGateId?: string | null;
  initialFocusMode?: FocusMode;
}

export interface UseGateFocusReturn<T extends GateSummary> {
  activeGateId: string | null;
  setActiveGateId: React.Dispatch<React.SetStateAction<string | null>>;
  focusMode: FocusMode;
  setFocusMode: React.Dispatch<React.SetStateAction<FocusMode>>;
  selectedGate: T | null;
  fitBoundsTrigger: number;
  handleFocusGate: (gateId: string) => void;
  handleZoomAll: () => void;
}

export function useGateFocus<T extends GateSummary>(
  gateSummaries: T[],
  options: UseGateFocusOptions = {},
): UseGateFocusReturn<T> {
  const { initialGateId = null, initialFocusMode = 'all' } = options;

  const [activeGateId, setActiveGateId] = useState<string | null>(initialGateId);
  const [focusMode, setFocusMode] = useState<FocusMode>(initialFocusMode);
  const [fitBoundsTrigger, setFitBoundsTrigger] = useState(0);

  const selectedGate = useMemo(
    () => gateSummaries.find((gate) => gate.id === activeGateId) ?? null,
    [gateSummaries, activeGateId],
  );

  const handleFocusGate = useCallback((gateId: string) => {
    setActiveGateId(gateId);
    setFocusMode('selected');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  const handleZoomAll = useCallback(() => {
    setFocusMode('all');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  return {
    activeGateId,
    setActiveGateId,
    focusMode,
    setFocusMode,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  };
}

export interface UseMapBoundsReturn {
  allBounds: LatLngBounds | null;
  selectedBounds: LatLngBounds | null;
  bounds: LatLngBounds | null;
  mapCenter: LatLngExpression;
  showBothGates: boolean;
  setShowBothGates: React.Dispatch<React.SetStateAction<boolean>>;
  handleMapBoundsChange: (shouldShow: boolean) => void;
}

export function useMapBounds<T extends GateSummary>(
  gateSummaries: T[],
  selectedGate: T | null,
  focusMode: FocusMode,
): UseMapBoundsReturn {
  const [showBothGates, setShowBothGates] = useState(false);

  const allBounds = useMemo(() => calculateAllBounds(gateSummaries), [gateSummaries]);
  const selectedBounds = useMemo(() => calculateSelectedBounds(selectedGate), [selectedGate]);
  const bounds = focusMode === 'selected' && selectedBounds ? selectedBounds : allBounds;
  const mapCenter = selectedGate?.start || gateSummaries[0]?.start || FALLBACK_CENTER;

  const handleMapBoundsChange = useCallback((shouldShow: boolean) => {
    setShowBothGates((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  return {
    allBounds,
    selectedBounds,
    bounds,
    mapCenter,
    showBothGates,
    setShowBothGates,
    handleMapBoundsChange,
  };
}
